import { ICSElement, ValidationRule, ValidationResult, ValidationError } from '../types/interfaces';
import * as Joi from 'joi';

/**
 * Расширенные типы валидации
 */
export enum ValidationType {
  REQUIRED = 'required',
  TYPE = 'type',
  RANGE = 'range',
  CUSTOM = 'custom',
  SCHEMA = 'schema',
  ASYNC = 'async',
  PATTERN = 'pattern',
  ENUM = 'enum',
  ARRAY = 'array',
  OBJECT = 'object'
}

/**
 * Результат асинхронной валидации
 */
export interface AsyncValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings?: ValidationError[];
  performance?: {
    executionTime: number;
    rulesExecuted: number;
  };
}

/**
 * Расширенное правило валидации
 */
export interface ExtendedValidationRule extends ValidationRule {
  type: ValidationType;
  schema?: Joi.Schema;
  async?: boolean;
  severity?: 'error' | 'warning' | 'info';
  dependencies?: string[];
  condition?: (element: ICSElement) => boolean;
}

/**
 * Кэш схем валидации
 */
class ValidationSchemaCache {
  private cache = new Map<string, Joi.Schema>();
  private maxSize = 100;

  get(key: string): Joi.Schema | undefined {
    return this.cache.get(key);
  }

  set(key: string, schema: Joi.Schema): void {
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, schema);
  }

  clear(): void {
    this.cache.clear();
  }
}

/**
 * Система валидации для CSElement
 */
export class ElementValidator {
  private rules: Map<string, ExtendedValidationRule[]> = new Map();
  private schemaCache = new ValidationSchemaCache();
  private globalSchemas: Map<string, Joi.Schema> = new Map();

  /**
   * Добавляет правило валидации
   */
  addRule(elementType: string, rule: ExtendedValidationRule): void {
    if (!this.rules.has(elementType)) {
      this.rules.set(elementType, []);
    }
    this.rules.get(elementType)!.push(rule);
  }

  /**
   * Добавляет схему Joi для типа элемента
   */
  addSchema(elementType: string, schema: Joi.Schema): void {
    this.globalSchemas.set(elementType, schema);
  }

  /**
   * Удаляет все правила для типа элемента
   */
  clearRules(elementType: string): void {
    this.rules.delete(elementType);
    this.globalSchemas.delete(elementType);
  }

  /**
   * Валидирует элемент (синхронно)
   */
  validate(element: ICSElement, elementType: string = 'default'): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const rules = this.rules.get(elementType) || [];

    // Сначала проверяем глобальную схему
    const globalSchema = this.globalSchemas.get(elementType);
    if (globalSchema) {
      const schemaResult = this.validateWithJoi(element, globalSchema);
      errors.push(...schemaResult.errors);
      warnings.push(...(schemaResult.warnings || []));
    }

    // Затем проверяем индивидуальные правила
    for (const rule of rules) {
      // Пропускаем асинхронные правила в синхронной валидации
      if (rule.async) continue;

      // Проверяем условие выполнения правила
      if (rule.condition && !rule.condition(element)) continue;

      const error = this.validateRule(element, rule);
      if (error) {
        if (rule.severity === 'warning') {
          warnings.push(error);
        } else {
          errors.push(error);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Асинхронная валидация элемента
   */
  async validateAsync(element: ICSElement, elementType: string = 'default'): Promise<AsyncValidationResult> {
    const startTime = Date.now();
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const rules = this.rules.get(elementType) || [];
    let rulesExecuted = 0;

    // Сначала выполняем синхронную валидацию
    const syncResult = this.validate(element, elementType);
    errors.push(...syncResult.errors);
    warnings.push(...(syncResult.warnings || []));
    rulesExecuted += rules.filter(r => !r.async).length;

    // Затем выполняем асинхронные правила
    const asyncRules = rules.filter(r => r.async);
    for (const rule of asyncRules) {
      if (rule.condition && !rule.condition(element)) continue;

      try {
        const error = await this.validateRuleAsync(element, rule);
        if (error) {
          if (rule.severity === 'warning') {
            warnings.push(error);
          } else {
            errors.push(error);
          }
        }
        rulesExecuted++;
             } catch (validationError) {
         errors.push({
           field: rule.field,
           message: `Ошибка валидации: ${validationError instanceof Error ? validationError.message : String(validationError)}`,
           value: this.getFieldValue(element, rule.field)
         });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      performance: {
        executionTime: Date.now() - startTime,
        rulesExecuted
      }
    };
  }

  /**
   * Валидация с использованием Joi схемы
   */
  private validateWithJoi(element: ICSElement, schema: Joi.Schema): { errors: ValidationError[], warnings?: ValidationError[] } {
    try {
      // Используем кэш для оптимизации
      const cacheKey = `${element.id}_${element.name}`;
      const cachedSchema = this.schemaCache.get(cacheKey);
      const validationSchema = cachedSchema || schema;
      
      // Кэшируем схему для будущих использований
      if (!cachedSchema) {
        this.schemaCache.set(cacheKey, schema);
      }

      const elementData = this.serializeElementForValidation(element);
      const result = validationSchema.validate(elementData, { abortEarly: false, allowUnknown: true });

      if (result.error) {
        const errors = result.error.details.map(detail => ({
          field: detail.path.join('.'),
          message: detail.message,
          value: detail.context?.value
        }));
        return { errors };
      }

      return { errors: [] };
         } catch (error) {
       return {
         errors: [{
           field: 'schema',
           message: `Ошибка схемы валидации: ${error instanceof Error ? error.message : String(error)}`,
           value: undefined
         }]
       };
     }
  }

  /**
   * Сериализует элемент для валидации
   */
     private serializeElementForValidation(element: ICSElement): any {
     return {
       id: element.id,
       name: element.name,
       index: element.index,
       data: element.getData ? element.getData('') : {},
       children: [] // Упрощаем - дочерние элементы не обязательны для валидации
     };
   }

  /**
   * Валидирует одно правило (синхронно)
   */
  private validateRule(element: ICSElement, rule: ExtendedValidationRule): ValidationError | null {
    const value = this.getFieldValue(element, rule.field);

    switch (rule.type) {
      case ValidationType.REQUIRED:
        if (value === undefined || value === null || value === '') {
          return {
            field: rule.field,
            message: rule.message || `Поле ${rule.field} обязательно для заполнения`,
            value
          };
        }
        break;

      case ValidationType.TYPE:
        if (value !== undefined && typeof value !== rule.value) {
          return {
            field: rule.field,
            message: rule.message || `Поле ${rule.field} должно быть типа ${rule.value}`,
            value
          };
        }
        break;

      case ValidationType.RANGE:
        if (typeof value === 'number' && rule.value) {
          const { min, max } = rule.value;
          if ((min !== undefined && value < min) || (max !== undefined && value > max)) {
            return {
              field: rule.field,
              message: rule.message || `Поле ${rule.field} должно быть в диапазоне [${min}, ${max}]`,
              value
            };
          }
        }
        break;

      case ValidationType.PATTERN:
        if (typeof value === 'string' && rule.value instanceof RegExp) {
          if (!rule.value.test(value)) {
            return {
              field: rule.field,
              message: rule.message || `Поле ${rule.field} не соответствует шаблону`,
              value
            };
          }
        }
        break;

      case ValidationType.ENUM:
        if (Array.isArray(rule.value) && !rule.value.includes(value)) {
          return {
            field: rule.field,
            message: rule.message || `Поле ${rule.field} должно быть одним из: ${rule.value.join(', ')}`,
            value
          };
        }
        break;

      case ValidationType.ARRAY:
        if (!Array.isArray(value)) {
          return {
            field: rule.field,
            message: rule.message || `Поле ${rule.field} должно быть массивом`,
            value
          };
        }
        break;

      case ValidationType.OBJECT:
        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
          return {
            field: rule.field,
            message: rule.message || `Поле ${rule.field} должно быть объектом`,
            value
          };
        }
        break;

      case ValidationType.SCHEMA:
        if (rule.schema) {
          const schemaResult = this.validateWithJoi({ [rule.field]: value } as any, Joi.object({ [rule.field]: rule.schema }));
          if (schemaResult.errors.length > 0) {
            return schemaResult.errors[0];
          }
        }
        break;

      case ValidationType.CUSTOM:
        if (rule.validator) {
          const result = rule.validator(value, element);
          if (typeof result === 'string') {
            return {
              field: rule.field,
              message: result,
              value
            };
          } else if (!result) {
            return {
              field: rule.field,
              message: rule.message || `Поле ${rule.field} не прошло валидацию`,
              value
            };
          }
        }
        break;
    }

    return null;
  }

  /**
   * Валидирует одно правило (асинхронно)
   */
  private async validateRuleAsync(element: ICSElement, rule: ExtendedValidationRule): Promise<ValidationError | null> {
    if (!rule.async || !rule.validator) {
      return null;
    }

    const value = this.getFieldValue(element, rule.field);
    
    try {
      const result = await rule.validator(value, element);
      if (typeof result === 'string') {
        return {
          field: rule.field,
          message: result,
          value
        };
      } else if (!result) {
        return {
          field: rule.field,
          message: rule.message || `Поле ${rule.field} не прошло асинхронную валидацию`,
          value
        };
      }
         } catch (error) {
       return {
         field: rule.field,
         message: `Ошибка асинхронной валидации: ${error instanceof Error ? error.message : String(error)}`,
         value
       };
     }

    return null;
  }

  /**
   * Получает значение поля элемента
   */
  private getFieldValue(element: ICSElement, field: string): any {
    switch (field) {
      case 'name':
        return element.name;
      case 'index':
        return element.index;
      case 'id':
        return element.id;
      default:
        // Проверяем в данных элемента
        return element.getData ? element.getData(field) : undefined;
    }
  }

  /**
   * Создает стандартные правила валидации
   */
  static createStandardRules(): ElementValidator {
    const validator = new ElementValidator();

    // Правила для базового элемента
    validator.addRule('default', {
      field: 'name',
      type: ValidationType.TYPE,
      value: 'string',
      message: 'Имя элемента должно быть строкой'
    });

    validator.addRule('default', {
      field: 'index',
      type: ValidationType.TYPE,
      value: 'number',
      message: 'Индекс элемента должен быть числом'
    });

    validator.addRule('default', {
      field: 'index',
      type: ValidationType.RANGE,
      value: { min: 0 },
      message: 'Индекс элемента должен быть неотрицательным'
    });

    validator.addRule('default', {
      field: 'id',
      type: ValidationType.REQUIRED,
      message: 'ID элемента обязательно'
    });

    validator.addRule('default', {
      field: 'id',
      type: ValidationType.PATTERN,
      value: /^[a-zA-Z0-9_-]+$/,
      message: 'ID элемента может содержать только буквы, цифры, дефисы и подчеркивания'
    });

    // Схема для базового элемента
    const baseElementSchema = Joi.object({
      id: Joi.string().required().pattern(/^[a-zA-Z0-9_-]+$/),
      name: Joi.string().optional(),
      index: Joi.number().integer().min(0).optional(),
      data: Joi.object().optional(),
      children: Joi.array().optional()
    });

    validator.addSchema('default', baseElementSchema);

    return validator;
  }

  /**
   * Создает расширенные правила валидации для типизированных элементов
   */
  static createTypedElementRules(): ElementValidator {
    const validator = ElementValidator.createStandardRules();

    // Правила для типизированных элементов
    validator.addRule('typed', {
      field: 'schema',
      type: ValidationType.REQUIRED,
      message: 'Типизированный элемент должен иметь схему'
    });

    validator.addRule('typed', {
      field: 'version',
      type: ValidationType.TYPE,
      value: 'string',
      message: 'Версия схемы должна быть строкой'
    });

    // Асинхронная валидация схемы
    validator.addRule('typed', {
      field: 'data',
      type: ValidationType.CUSTOM,
      async: true,
      validator: async (_value, element) => {
        // Имитация асинхронной проверки схемы
        return new Promise((resolve) => {
          setTimeout(() => {
            if (element.getData && typeof element.getData('schema') === 'string') {
              resolve(true);
            } else {
              resolve('Схема типизированного элемента недействительна');
            }
          }, 10);
        });
      },
      message: 'Данные не соответствуют схеме типизированного элемента'
    });

    return validator;
  }
}

/**
 * Глобальный валидатор по умолчанию
 */
export const defaultValidator = ElementValidator.createStandardRules();

/**
 * Валидатор для типизированных элементов
 */
export const typedElementValidator = ElementValidator.createTypedElementRules();

/**
 * Утилиты для создания схем Joi
 */
export class ValidationSchemaBuilder {
  static createElementSchema(options: {
    requiredFields?: string[];
    optionalFields?: string[];
    customValidators?: Record<string, Joi.Schema>;
  } = {}): Joi.Schema {
    const { requiredFields = [], optionalFields = [], customValidators = {} } = options;

    let schema = Joi.object({
      id: Joi.string().required(),
      name: Joi.string().optional(),
      index: Joi.number().integer().min(0).optional(),
      data: Joi.object().optional(),
      children: Joi.array().optional()
    });

    // Добавляем обязательные поля
    for (const field of requiredFields) {
      schema = schema.keys({
        [field]: customValidators[field] || Joi.any().required()
      });
    }

    // Добавляем опциональные поля
    for (const field of optionalFields) {
      schema = schema.keys({
        [field]: customValidators[field] || Joi.any().optional()
      });
    }

    return schema;
  }

  static createDataSchema(dataStructure: Record<string, any>): Joi.Schema {
    const schemaObject: Record<string, Joi.Schema> = {};

    for (const [key, value] of Object.entries(dataStructure)) {
      if (typeof value === 'string') {
        schemaObject[key] = Joi.string();
      } else if (typeof value === 'number') {
        schemaObject[key] = Joi.number();
      } else if (typeof value === 'boolean') {
        schemaObject[key] = Joi.boolean();
      } else if (Array.isArray(value)) {
        schemaObject[key] = Joi.array();
      } else if (typeof value === 'object' && value !== null) {
        schemaObject[key] = Joi.object();
      } else {
        schemaObject[key] = Joi.any();
      }
    }

    return Joi.object(schemaObject);
  }
} 